/**
 * Activity feed — "Argo did this": one honest record per agent run.
 *
 * Every agent run (scheduled by the live ticker, fired by the global
 * autonomy loop, or a human's "Run now") appends exactly one entry here,
 * written by `runAgentOnce` itself so no run path can forget to report.
 * The summary is derived from the run's REAL results — the counts the run
 * producers actually returned (records scanned, issues found, proposals
 * queued) — never a fabricated narration. Home renders the newest ~8 as
 * the "Argo has been working" feed with an honest empty state.
 *
 * Persistence rides the generic store (`agentFeed` collection registered
 * in src/lib/db/types.ts), so it works identically on localStorage in dev
 * and Firestore in prod. Append-only by contract: this module exposes
 * append + reads, never update/delete.
 */

import { db, nowIso, uuid } from '../db/store';

/** One "Argo did this" line. */
export interface AgentFeedEntry {
  id: string;
  /** ISO-8601 UTC timestamp of the run. */
  ts: string;
  /** The deployed agent that ran. */
  agentId: string;
  agentName: string;
  /**
   * What actually happened, e.g. "scanned 42 records, found 3 issues,
   * queued 2 proposals" — built from real run counts via `runSummary`,
   * or the run's own narration when a template has no counting producer.
   */
  summary: string;
  /** How many proposals this run queued into the approvals inbox. */
  proposalsQueued: number;
  /** What fired the run: the live/autonomy schedule, or a human click. */
  trigger: 'schedule' | 'manual';
}

/** Real counts a run produced — the raw material for an honest feed line. */
export interface RunStats {
  /** How many records the run actually read (tickets, people, docs, …). */
  scanned: number;
  /** How many issues/findings the sweep surfaced. */
  found: number;
  /** How many proposals were queued for approval (never auto-written). */
  queued: number;
}

/** How many entries the Home feed shows. */
export const FEED_LIMIT = 8;

const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? '' : 's'}`;

/** PURE: real run counts → the canonical feed line. */
export function runSummary(stats: RunStats): string {
  return (
    `scanned ${plural(stats.scanned, 'record')}, ` +
    `found ${plural(stats.found, 'issue')}, ` +
    `queued ${plural(stats.queued, 'proposal')}`
  );
}

/** New-entry input: the store stamps id (+ ts when omitted). */
export type NewFeedEntry = Omit<AgentFeedEntry, 'id' | 'ts'> & { ts?: string };

/**
 * Append one run record. Callers treat this as best-effort (a feed write
 * must never break the run it reports), but it is the ONLY writer — the
 * feed contains real runs or nothing.
 */
export async function appendFeedEntry(entry: NewFeedEntry): Promise<AgentFeedEntry> {
  const record: AgentFeedEntry = { id: uuid(), ts: entry.ts ?? nowIso(), ...entry } as AgentFeedEntry;
  await db.add('agentFeed', record);
  return record;
}

/** All feed entries, newest-first. */
export async function listFeed(): Promise<AgentFeedEntry[]> {
  const all = await db.list('agentFeed');
  return [...all].sort((a, b) => (a.ts < b.ts ? 1 : -1));
}

/** Subscribe to the feed (newest-first). Returns an unsubscribe fn. */
export function subscribeFeed(fn: (entries: AgentFeedEntry[]) => void): () => void {
  return db.subscribe('agentFeed', (rows) =>
    fn([...rows].sort((a, b) => (a.ts < b.ts ? 1 : -1))),
  );
}

/** Compact relative-time label for feed rows ("just now", "12m ago"). */
export function feedRelativeTime(iso: string, now = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const diff = Math.max(0, now - t);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
